import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import type { RegenerateTranscriptionRequestBody } from "@/common/types/regenerateTranscription";
import {
  abandonTranscriptionSubmission,
  completeTranscriptionSubmission,
  prepareTranscriptionSubmission,
  readTranscriptionSubmission,
  type TranscriptionSubmission,
} from "@/lib/transcription-submission";
import {
  getVisitScope,
  refreshVisitReads,
  useVisitScope,
} from "@/lib/visit-queries";
import { createTranscription } from "@/services/regenerateTranscription";

const inFlight = new Map<
  string,
  { promise: Promise<"audio" | "notes">; controller: AbortController }
>();
const message = (error: unknown) =>
  error instanceof Error ? error.message : t("visit.recovery.requestUncertain");
export const useRegenerateTranscription = (visitPublicId: string) => {
  const scope = useVisitScope();
  const account = scope ? `${scope.uid}:${scope.accountPublicId}` : null;
  const active = useRef({ scope, visitPublicId });
  if (
    active.current.scope !== scope ||
    active.current.visitPublicId !== visitPublicId
  )
    active.current = { scope, visitPublicId };
  const lifetime = active.current;
  const mounted = useRef(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<"audio" | "notes" | null>(null);
  const [, update] = useState(0);
  const current = () =>
    mounted.current &&
    active.current === lifetime &&
    !!scope &&
    getVisitScope() === scope;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const operationKey = scope
    ? `${account}:${scope.generation}:${visitPublicId}`
    : "";
  useEffect(() => {
    setLoading(false);
    setError(null);
    setResult(null);
    return () => {
      if (lifetime.scope) inFlight.get(operationKey)?.controller.abort();
    };
  }, [lifetime, operationKey]);
  let saved: TranscriptionSubmission | null = null;
  let storageError: string | null = null;
  if (account)
    try {
      saved = readTranscriptionSubmission(account, visitPublicId);
    } catch (failure) {
      storageError = message(failure);
    }
  const run = async (
    body?: RegenerateTranscriptionRequestBody,
  ): Promise<void> => {
    if (!current() || !scope || !account) return;
    setLoading(true);
    setError(null);
    setResult(null);
    let attempted = false;
    try {
      if (body && body.visitPublicId !== visitPublicId)
        throw Error("Visit changed. Select it again before regenerating.");
      const previous = readTranscriptionSubmission(account, visitPublicId);
      if (storageError) throw Error(storageError);
      const pending = body
        ? prepareTranscriptionSubmission(account, visitPublicId, {
            providerId: body.audioTranscriptionProviderId,
          })
        : previous;
      if (!pending) throw Error("No saved request to retry.");
      let operation = inFlight.get(operationKey);
      if (!operation) {
        const controller = new AbortController();
        const promise = (async (): Promise<"audio" | "notes"> => {
          if (!current()) throw Error("Account changed before submitting.");
          return (
            await createTranscription(visitPublicId, pending.body, {
              key: pending.key,
              userUid: scope.uid,
              signal: controller.signal,
            })
          ).mode;
        })().finally(() => {
          if (inFlight.get(operationKey)?.controller === controller)
            inFlight.delete(operationKey);
        });
        operation = { promise, controller };
        inFlight.set(operationKey, operation);
      }
      attempted = true;
      const mode = await operation.promise;
      if (!current()) return;
      completeTranscriptionSubmission(account, visitPublicId, pending, mode);
      setResult(mode);
    } catch (failure) {
      if (current())
        setError(
          t("visit.recovery.mayHaveSucceeded", { reason: message(failure) }),
        );
    } finally {
      if (attempted && getVisitScope() === scope) void refreshVisitReads(scope);
      if (current()) {
        setLoading(false);
        update((n) => n + 1);
      }
    }
  };
  const abandon = () => {
    if (!current() || !account || inFlight.has(operationKey)) return;
    try {
      abandonTranscriptionSubmission(account, visitPublicId);
      setError(null);
      update((n) => n + 1);
    } catch (failure) {
      setError(message(failure));
    }
  };
  return {
    regenerate: run,
    retry: () => run(),
    abandon,
    result,
    loading,
    error: error ?? storageError,
    recovery: saved?.kind === "pending" || !!storageError,
    savedProvider: saved?.kind === "pending" ? saved.body.providerId : null,
  };
};
